'use client';

import React, { useState, useEffect, useCallback } from 'react';

import { learningHubApi } from '@/lib/api';
import CourseCard from './CourseCard';
import { starterCourses } from './starterCourses';
import { AcademicCapIcon, BookOpenIcon, ClockIcon, FireIcon, StarIcon } from '@heroicons/react/24/outline';

export interface Course {
  id: string;
  title: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  lessons: number;
  instructor: string;
  description: string;
  progress: number;
  enrolled: number;
  featured: boolean;
  tags: string[];
  lessonLinks?: { title: string; url: string; source: string }[];
}

interface Article {
  id: string;
  title: string;
  category: string;
  readTime: string;
  date: string;
  excerpt: string;
}

function mapCourseRow(row: any, progressMap: Record<string, number>): Course {
  return {
    id: row.id,
    title: row.title,
    category: row.category,
    difficulty: row.difficulty as Course['difficulty'],
    duration: row.duration ?? '',
    lessons: row.lessons ?? 0,
    instructor: row.instructor ?? '',
    description: row.description ?? '',
    progress: progressMap[row.id] ?? 0,
    enrolled: row.enrolled ?? 0,
    featured: row.is_featured ?? false,
    tags: Array.isArray(row.tags) ? row.tags : (row.tags ? JSON.parse(row.tags) : []),
  };
}

const categories = ['All', 'Crypto Basics', 'Forex', 'Blockchain', 'DeFi', 'Risk Management', 'Scam Prevention'];
const difficulties = ['All Levels', 'Beginner', 'Intermediate', 'Advanced'];

const categoryColors: Record<string, string> = {
  'Crypto Basics': 'bg-warning/10 text-warning',
  Forex: 'bg-accent/10 text-accent',
  Blockchain: 'bg-purple-400/10 text-purple-400',
  DeFi: 'bg-positive/10 text-positive',
  'Risk Management': 'bg-orange-400/10 text-orange-400',
  'Scam Prevention': 'bg-negative/10 text-negative',
};

const articleCategoryColors: Record<string, string> = {
  'Scam Prevention': 'bg-negative/10 text-negative',
  DeFi: 'bg-positive/10 text-positive',
  Forex: 'bg-accent/10 text-accent',
};

export default function LearningHubContent() {
  const [category, setCategory] = useState('All');
  const [difficulty, setDifficulty] = useState('All Levels');
  const [courses, setCourses] = useState<Course[]>(starterCourses);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const { data, error } = await learningHubApi.list();
    if (!error && data) {
      const rows = Array.isArray(data) ? data : (data as any)?.data ?? [];
      // rows may contain { courses, articles } or flat array
      const coursesData = rows.courses ?? rows;
      const articlesData = rows.articles ?? [];
      const progressMap: Record<string, number> = {};
      if (rows.progress) {
        (rows.progress as any[]).forEach((p: any) => { progressMap[p.course_id] = p.progress; });
      }
      if (Array.isArray(coursesData)) {
        const stored = coursesData.map((row: any) => mapCourseRow(row, progressMap));
        setCourses([...starterCourses, ...stored.filter((course: Course) => !starterCourses.some((starter) => starter.id === course.id))]);
      }
      if (Array.isArray(articlesData)) {
        setArticles(articlesData.map((a: any) => ({
          id: a.id,
          title: a.title,
          category: a.category,
          readTime: a.read_time ?? '5 min read',
          date: new Date(a.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          excerpt: a.excerpt ?? '',
        })));
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = courses.filter((c) => {
    const matchCat = category === 'All' || c.category === category;
    const matchDiff = difficulty === 'All Levels' || c.difficulty === difficulty;
    return matchCat && matchDiff;
  });

  const activeCourses = courses.filter((c) => c.progress > 0);
  const totalProgress = activeCourses.length > 0
    ? Math.round(activeCourses.reduce((acc, c) => acc + c.progress, 0) / activeCourses.length)
    : 0;
  const completedCount = courses.filter((c) => c.progress === 100).length;
  const inProgressCount = courses.filter((c) => c.progress > 0 && c.progress < 100).length;

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl lg:text-2xl font-bold text-foreground">Learning Hub</h1>
        <p className="text-muted-foreground text-sm mt-1">Build financial literacy — understand before you invest</p>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-card border border-border rounded-xl p-3 lg:p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-[10px] lg:text-xs font-semibold uppercase tracking-wider">Risk Literacy</p>
            <StarIcon className="text-primary" style={{ width: 15, height: 15 }} />
          </div>
          <p className="font-mono-data text-2xl lg:text-3xl font-bold text-primary">74</p>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: '74%' }} />
          </div>
          <p className="text-muted-foreground text-[10px] lg:text-xs">of 100 — Intermediate</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 lg:p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-[10px] lg:text-xs font-semibold uppercase tracking-wider">Completed</p>
            <AcademicCapIcon className="text-positive" style={{ width: 15, height: 15 }} />
          </div>
          <p className="font-mono-data text-2xl lg:text-3xl font-bold text-positive">{completedCount}</p>
          <p className="text-muted-foreground text-[10px] lg:text-xs">of {courses.length} courses</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 lg:p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-[10px] lg:text-xs font-semibold uppercase tracking-wider">In Progress</p>
            <ClockIcon className="text-warning" style={{ width: 15, height: 15 }} />
          </div>
          <p className="font-mono-data text-2xl lg:text-3xl font-bold text-warning">{inProgressCount}</p>
          <p className="text-muted-foreground text-[10px] lg:text-xs">courses active</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 lg:p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-[10px] lg:text-xs font-semibold uppercase tracking-wider">Avg. Progress</p>
            <BookOpenIcon className="text-accent" style={{ width: 15, height: 15 }} />
          </div>
          <p className="font-mono-data text-2xl lg:text-3xl font-bold text-accent">{totalProgress}%</p>
          <p className="text-muted-foreground text-[10px] lg:text-xs">across active courses</p>
        </div>
      </div>

      {/* Featured Articles */}
      {articles.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4 lg:p-5">
          <div className="flex items-center gap-2 mb-4">
            <FireIcon className="text-primary" style={{ width: 16, height: 16 }} />
            <h3 className="text-sm font-semibold text-foreground">Featured Articles</h3>
          </div>
          <div className="flex gap-3 overflow-x-auto scrollbar-none pb-2 -mx-1 px-1 md:grid md:grid-cols-3 md:overflow-visible md:pb-0">
            {articles.map((article) => (
              <div
                key={article.id}
                className="flex-shrink-0 w-64 md:w-auto bg-muted rounded-xl p-4 space-y-2 cursor-pointer hover:bg-secondary transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${articleCategoryColors[article.category] ?? 'bg-muted text-muted-foreground'}`}>
                    {article.category}
                  </span>
                  <span className="text-xs text-muted-foreground">{article.readTime}</span>
                </div>
                <p className="text-sm font-semibold text-foreground leading-snug">{article.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{article.excerpt}</p>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-muted-foreground">{article.date}</span>
                  <button className="text-xs text-accent font-semibold hover:underline">Read →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 lg:p-4 space-y-3">
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1 -mx-1 px-1">
          {categories.map((cat) => (
            <button
              key={`cat-filter-${cat}`}
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 ${
                category === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1 -mx-1 px-1 items-center">
          {difficulties.map((d) => (
            <button
              key={`diff-filter-${d}`}
              onClick={() => setDifficulty(d)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 ${
                difficulty === d ? 'bg-secondary text-foreground border border-border' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {d}
            </button>
          ))}
          <span className="text-muted-foreground text-xs ml-2 flex-shrink-0">{filtered.length} courses</span>
        </div>
      </div>

      {/* Course Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4">
          {filtered.map((course) => (
            <CourseCard key={course.id} course={course} categoryColors={categoryColors} />
          ))}
        </div>
      )}

      {/* Sponsored */}
      <div className="bg-card border border-primary/20 rounded-xl p-3 lg:p-4 flex items-start sm:items-center gap-3">
        <span className="sponsored-tag flex-shrink-0">Sponsored</span>
        <p className="text-sm text-muted-foreground flex-1">
          <span className="text-foreground font-semibold">TradingView Pro</span> — Advanced charting for crypto and forex. 40% discount for BizzBox graduates.
        </p>
        <button className="text-xs text-primary font-semibold hover:underline flex-shrink-0 hidden sm:block">Claim →</button>
      </div>
    </div>
  );
}
